<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import FirmwareUpdateBadge from './FirmwareUpdateBadge.vue';
import TransportGuidance from './TransportGuidance.vue';
import { detectTransportSupport, TRANSPORT_OK } from '../js/transportSupport.js';
import HttpDeviceConnection from '../js/HttpDeviceConnection.js';
import KnownDevices from '../js/KnownDevices.js';

library.add(faTrash);

export default {
  name: "Home",

  components: {
    TransportGuidance,
    FontAwesomeIcon,
    FirmwareUpdateBadge
  },

  data() {
    return {
      knownDevices: {},
      // { [uid]: { status: 'probing'|'online'|'offline', probeResult: null|object } }
      knownDeviceStatuses: {},
    };
  },

  computed: {
    // False in Safari, Firefox and on iOS, where neither transport works.
    // Gates the connect hero and, in ConnectionStatus, the transport buttons
    // themselves (MDL-392).
    canReachDevice() {
      return detectTransportSupport().advice === TRANSPORT_OK;
    },

    deviceNeedsUpdate() {
      return (device) => {
        if (!this.latestFirmwareVersion) return false;
        return device.firmwareVersion.isOlderThan(this.latestFirmwareVersion);
      };
    },

    unattachedKnownDevices() {
      const attachedUids = new Set(this.attachedDevices.map(d => d.uid).filter(Boolean));
      const attachedUrls = new Set(this.attachedDevices.map(d => d.baseUrl).filter(Boolean));
      return Object.values(this.knownDevices).filter(entry => {
        return !attachedUids.has(entry.uid) && !attachedUrls.has(`http://${entry.host}`);
      });
    },
  },

  mounted() {
    this.loadAndProbeKnownDevices();
  },

  methods: {
    loadAndProbeKnownDevices() {
      this.knownDevices = KnownDevices.getAll();
      for (const entry of Object.values(this.knownDevices)) {
        this.probeKnownDevice(entry);
      }
    },

    probeKnownDevice(entry) {
      const { uid, host } = entry;
      this.knownDeviceStatuses = { ...this.knownDeviceStatuses, [uid]: { status: 'probing', probeResult: null } };
      HttpDeviceConnection.probe(`http://${host}`, 2000)
        .then(result => {
          this.knownDeviceStatuses = {
            ...this.knownDeviceStatuses,
            [uid]: { status: 'online', probeResult: { baseUrl: `http://${host}`, ...result } },
          };
        })
        .catch(() => {
          this.knownDeviceStatuses = { ...this.knownDeviceStatuses, [uid]: { status: 'offline', probeResult: null } };
        });
    },

    async connectKnownDevice(uid) {
      const entry = this.knownDevices[uid];
      if (!entry) return;
      let probeResult = this.knownDeviceStatuses[uid]?.probeResult;
      if (!probeResult) {
        try {
          const result = await HttpDeviceConnection.probe(`http://${entry.host}`, 3000);
          probeResult = { baseUrl: `http://${entry.host}`, ...result };
        } catch {
          return;
        }
      }
      const baseUrl = probeResult.baseUrl;
      const filtered = this.attachedDevices.filter(d => d.baseUrl !== baseUrl);
      const device = new HttpDeviceConnection(probeResult);
      this.$emit('devicesUpdated', [...filtered, device]);
      this.$emit('deviceSelected', device);
      KnownDevices.save(uid, entry.host, probeResult.version);
      this.knownDevices = KnownDevices.getAll();
    },

    connectToDevice(device) {
      this.$emit('deviceSelected', device);
    }
  },

  emits: [
    'alert',
    'deviceError',
    'deviceSelected',
    'devicesUpdated',
    'updateEffect'
  ],

  props: {
    attachedDevices: {
      type: Array,
      required: true
    },

    capabilities: {
      type: Object,
      required: true,
    },

    config: {
      type: Object,
      required: true
    },

    connectedDevice: {
      type: Object,
      required: false
    },

    connectedToDevice: {
      type: Boolean,
      required: true
    },

    fileOpened: {
      type: Boolean,
      required: true
    },

    latestFirmwareVersion: {
      type: Object,
      required: false,
      default: null
    }
  }
}
</script>
<template>

<div>
  <div v-if="attachedDevices.length === 0 && unattachedKnownDevices.length === 0">
    <!-- Where the browser cannot reach a device at all, this replaces "Connect
         device" rather than sitting beside it. Told to connect something they
         cannot connect, someone goes looking for a cable — the instruction has
         to be the one that works (MDL-392). Renders nothing in Chromium, so
         the hero below is the normal case. -->
    <TransportGuidance v-if="!canReachDevice" />

    <div v-else class="hero">
      <div class="hero-content text-center">
        <div class="max-w-md">
          <h2 class="text-5xl font-bold">Connect device</h2>
          <p class="py-6">Please connect your Midulate device to get started.</p>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="flex justify-evenly gap-4 max-w-4xl mx-auto mt-8">
    <!-- Attached devices (USB or already-connected WiFi) -->
    <div v-for="device in attachedDevices" :key="device.baseUrl ?? device.connectionType" class="hero p-8 border-2">
      <div class="hero-content text-center">
        <div class="max-w-md">
          <h2 class="text-4xl uppercase font-russo tracking-wide font-stretch-ultra-condensed">Midulate</h2>
          <p class="text-lg pt-6 pb-0">Version: {{ device.firmwareVersion }}</p>
          <p class="text-sm pb-4 pt-0">{{ device.connection.description }}</p>
          <FirmwareUpdateBadge v-if="deviceNeedsUpdate(device)" class="mb-4" />
          <div class="mt-2">
            <button class="btn btn-primary text-lg" @click="connectToDevice(device)">Connect</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Known WiFi devices not yet attached -->
    <div v-for="entry in unattachedKnownDevices" :key="entry.uid" class="hero p-8 border-2">
      <div class="hero-content text-center">
        <div class="max-w-md">
          <h2 class="text-4xl uppercase font-russo tracking-wide font-stretch-ultra-condensed">Midulate</h2>
          <p class="text-lg pt-6 pb-0">Version: {{ knownDeviceStatuses[entry.uid]?.probeResult?.version ?? entry.version ?? '—' }}</p>
          <p class="text-sm pb-2 pt-0">
            <span v-if="knownDeviceStatuses[entry.uid]?.status === 'online'"    class="status status-success"></span>
            <span v-else-if="knownDeviceStatuses[entry.uid]?.status === 'offline'" class="status status-error"></span>
            <span v-else class="status status-neutral animate-ping"></span>
            WiFi: {{ entry.host }}
          </p>
          <div class="mt-2 flex flex-col gap-2 items-center">
            <button
              class="btn btn-primary text-lg"
              @click="connectKnownDevice(entry.uid)"
            >Connect</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

</template>
